import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Menu, Tooltip } from "antd";
import {
  PhoneOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import MapIcon from "../../assets/icons/007-map.png";
import FieldIcon from "../../assets/icons/002-agriculture.png";
import BoxIcon from "../../assets/icons/004-product.png";
import NetworkIcon from "../../assets/icons/005-network.png";
import UsersIcon from "../../assets/icons/006-add-group.png";

import MapItems from "./MapItems";
import { useSelector, useDispatch } from "react-redux";
import { loggedOut } from "../../service/auth.service";
import { clearUser } from "../../redux/slices/userSlice";

const { Sider } = Layout;

const SideBar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [mapExpanded, setMapExpanded] = useState(!collapsed);
  const userRole = useSelector((state) => 
    state.user.userRole ? state.user.userRole.name : null
  );

  const getNavigationItems = () => {
    const items = {
      admin: [
        {
          key: "/admin/client/all",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={UsersIcon} alt="users" className="w-5 h-5 object-contain" />
          </div>,
          label: "Liste d'Utilisateurs",
        },
        {
          key: "/admin/exploitation/all",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={FieldIcon} alt="field" className="w-5 h-5 object-contain" />
          </div>,
          label: "Exploitations",
        },
        {
          key: "/admin/products/all",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={BoxIcon} alt="box" className="w-5 h-5 object-contain" />
          </div>,
          label: "Produits",
        },
        {
          key: "/admin/domains/all",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={NetworkIcon} alt="network" className="w-5 h-5 object-contain" />
          </div>,
          label: "Domaines",
        },
        {
          key: "/admin/licenses",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <KeyOutlined />
          </div>,
          label: "Licences",
        },
      ],
      client: [
        {
          key: "/client/exploitation/all",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={FieldIcon} alt="field" className="w-5 h-5 object-contain" />
          </div>,
          label: "Mes exploitations",
        },
        {
          key: "/client/profile",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <UserOutlined />
          </div>,
          label: "Mon profil",
        },
        {
          key: "/client/associated-domains",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={NetworkIcon} alt="network" className="w-5 h-5 object-contain" />
          </div>,
          label: "Domaines associés",
        }
      ],
      domain_manager: [
        {
          key: "/domain-manager/exploitations",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={FieldIcon} alt="field" className="w-5 h-5 object-contain" />
          </div>,
          label: "Exploitations",
        },
        {
          key: "/domain-manager/profile",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <UserOutlined />
          </div>,
          label: "Mon profil",
        },
        {
          key: "/domain-manager/associated-domains",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={NetworkIcon} alt="network" className="w-5 h-5 object-contain" />
          </div>,
          label: "Domaines associés",
        }
      ],
      exploitation_manager: [
        {
          key: "/exploitation-manager/exploitations",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <img src={FieldIcon} alt="field" className="w-5 h-5 object-contain" />
          </div>,
          label: "Mes exploitations",
        },
        {
          key: "/exploitation-manager/profile",
          icon: <div className="flex items-center justify-center w-6 h-6">
            <UserOutlined />
          </div>,
          label: "Mon profil",
        }
      ]
    };

    return (userRole === "superadmin" ? items.admin : items[userRole]) || [];
  };

  const navigationItems = getNavigationItems();

  const footerItems = [
    {
      key: "/contact",
      icon: <div className="flex items-center justify-center w-6 h-6">
        <PhoneOutlined />
      </div>,
      label: "Contact",
    },
    {
      key: "logout",
      icon: <div className="flex items-center justify-center w-6 h-6">
        <LogoutOutlined />
      </div>,
      label: "Se déconnecter",
      danger: true,
    }
  ];

  const handleLogout = () => {
    dispatch(clearUser());
    loggedOut();
    navigate("/login");
  };

  const handleClick = (e) => {
    if (e.key === 'logout') {
      handleLogout();
    } else {
      navigate(e.key);
    }
  };

  const handleMapClick = () => {
    if (collapsed) {
      onToggle();
    }
    setMapExpanded(!mapExpanded);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={250}
      collapsedWidth={80}
      className="fixed left-0 top-0 bottom-0 overflow-auto h-screen"
      style={{ backgroundColor: '#EEF2FF', zIndex: 10 }}
    >
      <div className="flex flex-col justify-between h-full">
        <div>
          <div className="h-16 flex items-center justify-between px-4">
            {!collapsed && <span className="text-lg font-semibold">Navigation</span>}
            <button
              onClick={onToggle}
              className="p-2 pl-4 rounded-md hover:bg-white/50"
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          </div>
          <div className="p-1">
            <Tooltip title={collapsed ? "Map" : ""} placement="right">
              <div
                className={`cursor-pointer rounded-lg hover:bg-gray-500/10 transition-colors duration-200 ${
                  collapsed ? 'px-3 py-3 bg-white/30' : ''
                }`}
                onClick={handleMapClick}
              >
                {collapsed && (
                  <div className="flex items-center justify-center w-6 h-6 ml-3">
                    <img src={MapIcon} alt="map" className="w-5 h-5 object-contain" />
                  </div>
                )}
              </div>
            </Tooltip>
            <MapItems collapsed={collapsed} />
          </div>

          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            onClick={handleClick}
            items={navigationItems}
            className="border-r-0 bg-transparent"
            style={{
              fontSize: '14px',
              lineHeight: '20px'
            }}
          />
        </div>
        <Menu
          mode="inline"
          selectable={false}
          items={footerItems}
          onClick={handleClick}
          className="border-r-0 bg-transparent mt-auto mb-6"
          style={{
            fontSize: '14px',
            lineHeight: '20px'
          }}
        />
      </div>
    </Sider>
  );
};

export default SideBar;